
import { useState, useEffect, useRef } from 'react';
import type React from 'react';

interface Project {
  id: number;
  title: string;
  brand: string;
  category: string;
  views: string;
  duration: string;
  thumbnail: string;
}

export default function ProjectShowcase() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredProject, setHoveredProject] = useState<number | null>(null);
  const [row1Index, setRow1Index] = useState(0);
  const [row2Index, setRow2Index] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollRef1 = useRef<HTMLDivElement>(null);
  const scrollRef2 = useRef<HTMLDivElement>(null);

  // -------------------------------------------------------------------------
  // Intersection observer – reveals section when it scrolls into view
  // -------------------------------------------------------------------------
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // -------------------------------------------------------------------------
  // Dummy data – in a real app this would come from an API
  // -------------------------------------------------------------------------
  const projectsRow1: Project[] = [
    {
      id: 1,
      title: 'Premium Skincare Routine',
      brand: 'GlowLab',
      category: 'Beauty',
      views: '125K',
      duration: '0:45',
      thumbnail:
        'https://readdy.ai/api/search-image?query=male%20hands%20applying%20premium%20skincare%20product%20serum%20on%20face%20close%20up%20vertical%20video%20format%20clean%20bright%20bathroom%20background%20soft%20natural%20lighting%20beauty%20routine%20content%20creation&width=450&height=800&seq=proj-v1&orientation=portrait',
    },
    {
      id: 2,
      title: 'Wireless Earbuds Unboxing',
      brand: 'SoundWave',
      category: 'Tech',
      views: '89K',
      duration: '1:20',
      thumbnail:
        'https://readdy.ai/api/search-image?query=modern%20wireless%20earbuds%20unboxing%20vertical%20video%20format%20hands%20opening%20premium%20packaging%20on%20clean%20white%20desk%20tech%20product%20reveal%20cinematic%20close%20up%20lighting&width=450&height=800&seq=proj-v2&orientation=portrait',
    },
    {
      id: 3,
      title: 'Fitness Gear Review',
      brand: 'ActiveLife',
      category: 'Lifestyle',
      views: '156K',
      duration: '2:15',
      thumbnail:
        'https://readdy.ai/api/search-image?query=athletic%20male%20demonstrating%20fitness%20resistance%20bands%20workout%20equipment%20vertical%20video%20format%20bright%20modern%20gym%20setting%20energetic%20lifestyle%20content%20creation&width=450&height=800&seq=proj-v3&orientation=portrait',
    },
    {
      id: 4,
      title: 'Coffee Maker Tutorial',
      brand: 'BrewMaster',
      category: 'Lifestyle',
      views: '92K',
      duration: '1:45',
      thumbnail:
        'https://readdy.ai/api/search-image?query=hands%20preparing%20artisan%20coffee%20with%20premium%20pour%20over%20coffee%20maker%20vertical%20video%20format%20marble%20kitchen%20countertop%20morning%20golden%20light%20cozy%20atmosphere%20lifestyle%20content&width=450&height=800&seq=proj-v4&orientation=portrait',
    },
  ];

  const projectsRow2: Project[] = [
    {
      id: 5,
      title: 'Smart Watch Features',
      brand: 'TechPulse',
      category: 'Tech',
      views: '203K',
      duration: '1:30',
      thumbnail:
        'https://readdy.ai/api/search-image?query=male%20wrist%20wearing%20modern%20smartwatch%20close%20up%20vertical%20video%20format%20showing%20fitness%20tracking%20data%20clean%20minimal%20white%20background%20tech%20product%20showcase%20professional%20lighting&width=450&height=800&seq=proj-v5&orientation=portrait',
    },
    {
      id: 6,
      title: 'Sneaker Collection',
      brand: 'UrbanStep',
      category: 'Fashion',
      views: '178K',
      duration: '2:00',
      thumbnail:
        'https://readdy.ai/api/search-image?query=premium%20sneakers%20being%20laced%20up%20close%20up%20vertical%20video%20format%20clean%20white%20floor%20dramatic%20side%20lighting%20fashion%20product%20showcase%20minimalist%20composition%20high%20contrast&width=450&height=800&seq=proj-v6&orientation=portrait',
    },
    {
      id: 7,
      title: 'Gaming Headset Review',
      brand: 'GameZone',
      category: 'Tech',
      views: '245K',
      duration: '3:20',
      thumbnail:
        'https://readdy.ai/api/search-image?query=male%20putting%20on%20premium%20gaming%20headset%20vertical%20video%20format%20atmospheric%20rgb%20lighting%20dark%20background%20tech%20enthusiast%20content%20creation%20cinematic%20close%20up&width=450&height=800&seq=proj-v7&orientation=portrait',
    },
    {
      id: 8,
      title: 'Protein Shake Recipe',
      brand: 'FitFuel',
      category: 'Lifestyle',
      views: '67K',
      duration: '0:55',
      thumbnail:
        'https://readdy.ai/api/search-image?query=male%20hands%20preparing%20healthy%20protein%20shake%20blending%20ingredients%20vertical%20video%20format%20bright%20kitchen%20counter%20natural%20lighting%20lifestyle%20fitness%20content%20creation&width=450&height=800&seq=proj-v8&orientation=portrait',
    },
  ];

  // -------------------------------------------------------------------------
  // Mobile navigation – cycles through the list
  // -------------------------------------------------------------------------
  const navigateMobile = (
    direction: 'left' | 'right',
    currentIndex: number,
    setIndex: (i: number) => void,
    total: number
  ) => {
    if (direction === 'left') {
      setIndex(currentIndex <= 0 ? total - 1 : currentIndex - 1);
    } else {
      setIndex(currentIndex >= total - 1 ? 0 : currentIndex + 1);
    }
  };

  // -------------------------------------------------------------------------
  // Desktop scroll – smooth scroll by a fixed amount
  // -------------------------------------------------------------------------
  const scrollDesktopRow = (
    ref: React.RefObject<HTMLDivElement>,
    direction: 'left' | 'right'
  ) => {
    if (!ref.current) return;
    const scrollAmount = 324;
    ref.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  // -------------------------------------------------------------------------
  // Single video card – reused in both carousel styles
  // -------------------------------------------------------------------------
  const renderVideoCard = (project: Project, fullWidth?: boolean) => (
    <div
      key={project.id}
      className={`${
        fullWidth ? 'w-full' : 'w-[260px] sm:w-[280px] md:w-[300px] flex-shrink-0'
      } relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl cursor-pointer group transition-all duration-500`}
      onMouseEnter={() => setHoveredProject(project.id)}
      onMouseLeave={() => setHoveredProject(null)}
    >
      <div className="aspect-[9/16] w-full relative overflow-hidden">
        <img
          src={project.thumbnail}
          alt={project.title}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#00D9FF] flex items-center justify-center transition-all duration-300 ${
              hoveredProject === project.id
                ? 'scale-110 opacity-100'
                : 'scale-90 opacity-70'
            }`}
          >
            <i className="ri-play-fill text-xl sm:text-2xl text-black ml-0.5"></i>
          </div>
        </div>

        <div className="absolute top-3 right-3 px-2.5 py-1 bg-white/20 backdrop-blur-md rounded-full flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-[#00D9FF]"></div>
          <span className="text-white text-[10px] sm:text-xs font-medium">
            {project.category}
          </span>
        </div>

        <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/50 backdrop-blur-md rounded-full">
          <span className="text-white text-[10px] sm:text-xs font-medium">
            {project.duration}
          </span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
          <h3 className="text-white text-sm sm:text-base font-bold mb-1 leading-tight">
            {project.title}
          </h3>
          <p className="text-gray-300 text-xs sm:text-sm font-light mb-2">
            {project.brand}
          </p>
          <div className="flex items-center gap-1.5 text-white/80 text-xs">
            <i className="ri-eye-line text-sm"></i>
            <span>{project.views} views</span>
          </div>
        </div>
      </div>
    </div>
  );

  // -------------------------------------------------------------------------
  // Carousel row – handles both desktop (scrollable) and mobile (single) UI
  // -------------------------------------------------------------------------
  const renderCarouselRow = (
    label: string,
    projects: Project[],
    currentIndex: number,
    setIndex: (i: number) => void,
    scrollRef: React.RefObject<HTMLDivElement>
  ) => {
    const currentProject = projects[currentIndex];
    if (!currentProject) return null;

    return (
      <div className="mb-12 sm:mb-16">
        {/* Header + controls */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <h3 className="text-white text-sm sm:text-base font-medium uppercase tracking-widest">
            {label}
          </h3>
          <div className="flex items-center gap-3">
            {/* Mobile counter */}
            <span className="text-gray-500 text-xs sm:text-sm font-medium md:hidden">
              {currentIndex + 1} / {projects.length}
            </span>

            {/* Mobile arrows */}
            <div className="flex gap-2 md:hidden">
              <button
                onClick={() =>
                  navigateMobile('left', currentIndex, setIndex, projects.length)
                }
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#00D9FF] hover:text-black transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              <button
                onClick={() =>
                  navigateMobile('right', currentIndex, setIndex, projects.length)
                }
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#00D9FF] hover:text-black transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>

            {/* Desktop arrows */}
            <div className="hidden md:flex gap-2">
              <button
                onClick={() => scrollDesktopRow(scrollRef, 'left')}
                className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#00D9FF] hover:text-black transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              <button
                onClick={() => scrollDesktopRow(scrollRef, 'right')}
                className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#00D9FF] hover:text-black transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Desktop: scrollable row */}
        <div
          ref={scrollRef}
          className="hidden md:flex gap-6 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {projects.map((project) => renderVideoCard(project))}
        </div>

        {/* Mobile: single card + dots */}
        <div className="md:hidden">
          <div className="flex justify-center items-center min-h-[420px] sm:min-h-[480px]">
            <div
              className={`w-[260px] sm:w-[280px] transition-all duration-500 ${
                isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}
            >
              {renderVideoCard(currentProject, true)}
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-6">
            {projects.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setIndex(idx)}
                className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  idx === currentIndex
                    ? 'w-8 bg-[#00D9FF]'
                    : 'w-1.5 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main component markup
  // -------------------------------------------------------------------------
  return (
    <section
      id="work"
      ref={sectionRef}
      className="py-16 sm:py-24 md:py-32 bg-[#0a0a0a] overflow-hidden"
    >
      {/* Hide native scrollbar */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-12 sm:mb-16 md:mb-20">
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="w-12 sm:w-16 h-1 bg-[#00D9FF] mb-4 sm:mb-6"></div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-3 sm:mb-4 tracking-tight">
              SELECTED WORK
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-500 font-light">
              Recent collaborations that drove results
            </p>
          </div>
        </div>

        {/* Carousels */}
        {renderCarouselRow(
          'Latest Projects',
          projectsRow1,
          row1Index,
          setRow1Index,
          scrollRef1
        )}
        {renderCarouselRow(
          'Fan Favorites',
          projectsRow2,
          row2Index,
          setRow2Index,
          scrollRef2
        )}

        {/* Load More */}
        <div className="text-center mt-8 sm:mt-12">
          <button className="border-2 border-[#00D9FF] text-[#00D9FF] px-6 sm:px-8 py-3 sm:py-4 rounded-full font-medium flex items-center gap-3 mx-auto hover:bg-[#00D9FF] hover:text-black transition-all duration-300 group cursor-pointer whitespace-nowrap text-sm sm:text-base">
            Load More Projects
            <i className="ri-arrow-down-line group-hover:translate-y-1 transition-transform duration-300"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
